/** Versioned siblings keep the uploaded original and database URL intact. */
export function therapistVideoAssetPaths(original: string) {
  const url = new URL(original);
  if (url.protocol !== "https:" || url.username || url.password || url.port)
    return null;
  if (!url.hostname.endsWith(".public.blob.vercel-storage.com")) return null;
  const base = `${url.origin}${url.pathname}.delivery-v1`;
  return {
    manifest: `${base}.json`,
    failed: `${base}.failed.json`,
    preview: `${base}.preview.mp4`,
    full: `${base}.full.mp4`,
  };
}

/** Only therapist-video callers include these siblings in replacement/erasure cleanup. */
export function therapistVideoFileUrls(original: string) {
  try {
    const paths = therapistVideoAssetPaths(original);
    return paths ? [original, ...Object.values(paths)] : [original];
  } catch {
    return [original];
  }
}

const resolutions = new Map<
  string,
  {
    expires: number;
    promise: Promise<ReturnType<typeof therapistVideoAssetPaths>>;
  }
>();

/** Call only on hover/play intent. Failed or pending processing uses the original. */
export async function resolveTherapistVideo(
  original: string,
  kind: "preview" | "full",
) {
  let paths: ReturnType<typeof therapistVideoAssetPaths>;
  try {
    paths = therapistVideoAssetPaths(original);
  } catch {
    return original;
  }
  if (!paths) return original;
  const cached = resolutions.get(original);
  let promise =
    cached?.expires && cached.expires > Date.now() ? cached.promise : undefined;
  if (!promise) {
    promise = fetch(paths.manifest, { signal: AbortSignal.timeout(1500) })
      .then(async (response) => {
        if (!response.ok) return null;
        const manifest = (await response.json()) as {
          version?: unknown;
          original?: unknown;
        };
        return manifest.version === 1 && manifest.original === original
          ? paths
          : null;
      })
      .catch(() => null);
    resolutions.set(original, { expires: Date.now() + 60_000, promise });
  }
  return (await promise)?.[kind] ?? original;
}
